import React, { useState, useEffect, useCallback } from "react";
import {
  FaChrome,
  FaCheckCircle,
  FaExclamationTriangle,
  FaTrash,
  FaSyncAlt,
  FaPlug,
  FaPaperPlane,
} from "react-icons/fa";
import api from "../utils/axiosInstance";
import { socket } from "../socket";
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Alert,
  PageHeader,
  TableWrapper,
  Table,
  Th,
  Td,
  Badge,
  Spinner,
  EmptyState,
} from "../components/ui";

/**
 * BrowserSessions — connect a WhatsApp number via the Walytic Chrome
 * extension instead of a QR code / datacenter IP. The extension relays the
 * user's real logged-in web.whatsapp.com session to Walytic Cloud.
 *
 * Endpoints: GET /api/browser/sessions, DELETE /api/browser/sessions/:id.
 */
export default function BrowserSessions() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState({});

  const fetchSessions = useCallback(async () => {
    try {
      const res = await api.get("/api/browser/sessions");
      setSessions(res?.data?.sessions || []);
    } catch (err) {
      console.error("Failed to load browser sessions:", err);
      if (err?.response?.status !== 404) {
        setError(err?.response?.data?.error || err.message);
      } else {
        setSessions([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  // Real-time refresh on session lifecycle events.
  useEffect(() => {
    const onEvent = () => fetchSessions();
    socket.on("session_update", onEvent);
    socket.on("connected", onEvent);
    socket.on("disconnected", onEvent);
    return () => {
      socket.off("session_update", onEvent);
      socket.off("connected", onEvent);
      socket.off("disconnected", onEvent);
    };
  }, [fetchSessions]);

  const handleDelete = async (sessionId) => {
    if (!window.confirm("Disconnect this browser session? It will stop relaying messages.")) return;
    setBusy((b) => ({ ...b, [sessionId]: "delete" }));
    try {
      await api.delete(`/api/browser/sessions/${sessionId}`);
      await fetchSessions();
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setBusy((b) => ({ ...b, [sessionId]: null }));
    }
  };

  const handleTestSend = async (sessionId) => {
    const to = window.prompt("Send a test message — recipient phone (E.164, digits only):");
    if (!to) return;
    const text = window.prompt("Message text:", "Hello from Walytic — browser relay test");
    if (!text) return;
    setBusy((b) => ({ ...b, [sessionId]: "send" }));
    try {
      const res = await api.post(`/api/browser/sessions/${sessionId}/test-send`, { to, text });
      if (!res?.data?.success) throw new Error(res?.data?.error || "Send failed");
      window.alert(`Sent. messageId: ${res.data.messageId || "(none)"}`);
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setBusy((b) => ({ ...b, [sessionId]: null }));
    }
  };

  const connectedCount = sessions.filter((s) => s.status === "connected").length;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <PageHeader
        title="Connect Browser"
        subtitle="Link a WhatsApp number through your own Chrome browser — no QR code, no datacenter IP. Walytic Cloud drives campaigns, flows and the Unibox; the extension relays through your logged-in WhatsApp Web session."
      />

      {error && (
        <Alert variant="error" className="mb-4">
          <FaExclamationTriangle className="inline mr-2" />
          {error}
        </Alert>
      )}

      {/* How to connect */}
      <Card className="mb-6">
        <CardHeader>
          <h2 className="text-lg font-semibold">
            <FaChrome className="inline mr-2 text-brand" />
            How to connect
          </h2>
        </CardHeader>
        <CardBody>
          <ol className="space-y-3 text-sm text-gray-700">
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-brand text-white text-xs flex items-center justify-center font-semibold">1</span>
              <span>Install the <strong>Walytic Browser Relay</strong> extension and pin it.</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-brand text-white text-xs flex items-center justify-center font-semibold">2</span>
              <span>Open <span className="font-mono">web.whatsapp.com</span> and log in (the phone is already linked to that browser). Keep the tab open.</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-brand text-white text-xs flex items-center justify-center font-semibold">3</span>
              <span>Click the extension icon and sign in with your <strong>Walytic email &amp; password</strong>. The session appears below once connected.</span>
            </li>
          </ol>
          <p className="text-xs text-gray-400 mt-4">
            The extension authenticates with your Walytic credentials — it never accepts anonymous connections. Messages flow only while that browser tab is open.
          </p>
        </CardBody>
      </Card>

      {/* Sessions */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">
              Browser sessions
              {!loading && sessions.length > 0 && (
                <Badge className="ml-2" variant="success">{connectedCount} connected</Badge>
              )}
            </h2>
            <Button variant="ghost" onClick={fetchSessions} title="Refresh">
              <FaSyncAlt />
            </Button>
          </div>
        </CardHeader>
        <CardBody>
          {loading ? (
            <Spinner />
          ) : sessions.length === 0 ? (
            <EmptyState
              icon={FaPlug}
              title="No browser sessions yet"
              description="Install the extension and sign in to connect your first number — no QR code required."
            />
          ) : (
            <TableWrapper>
              <Table>
                <thead>
                  <tr>
                    <Th>Number</Th>
                    <Th>Device</Th>
                    <Th>Status</Th>
                    <Th>Last activity</Th>
                    <Th className="text-right">Actions</Th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((s) => {
                    const live = s.status === "connected" && s.live;
                    return (
                      <tr key={s.sessionId}>
                        <Td>
                          <span className="font-mono">{s.number ? `+${s.number}` : "—"}</span>
                        </Td>
                        <Td>
                          <span className="text-xs font-mono text-gray-600">{s.browserDeviceId || s.sessionId}</span>
                        </Td>
                        <Td>
                          <Badge variant={live ? "success" : s.status === "connected" ? "warning" : "error"}>
                            {live ? "connected" : s.status}
                            {s.status === "connected" && !s.live ? " (tab closed)" : ""}
                          </Badge>
                        </Td>
                        <Td>
                          <span className="text-xs text-gray-500">
                            {s.lastMessageTime ? new Date(s.lastMessageTime).toLocaleString() : "—"}
                          </span>
                        </Td>
                        <Td className="text-right whitespace-nowrap">
                          <Button
                            variant="ghost"
                            onClick={() => handleTestSend(s.sessionId)}
                            disabled={!!busy[s.sessionId]}
                            title="Send a test message"
                          >
                            {busy[s.sessionId] === "send" ? <Spinner size="sm" /> : <FaPaperPlane />}
                          </Button>
                          <Button
                            variant="ghost"
                            onClick={() => handleDelete(s.sessionId)}
                            disabled={!!busy[s.sessionId]}
                            title="Disconnect this browser session"
                          >
                            {busy[s.sessionId] === "delete" ? <Spinner size="sm" /> : <FaTrash className="text-red-500" />}
                          </Button>
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </TableWrapper>
          )}
        </CardBody>
      </Card>

      <div className="mt-4 flex items-start gap-2 text-xs text-gray-400">
        <FaCheckCircle className="mt-0.5 text-green-500" />
        <span>
          Connected browser sessions appear alongside your QR-linked numbers in WhatsApp Phones and the Unibox, and are selectable when sending campaigns and flows.
        </span>
      </div>
    </div>
  );
}
